import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../../components/SEO";
import { api } from "../../lib/api";
import "./hpa.css";

/**
 * Block 11.2 · § 32 · Statusabhängige Home
 *
 *   NOT_STARTED     → "HPA starten"
 *   ACTIVE (IN_INTAKE / DRAFT)  → "HPA fortsetzen"
 *   PAUSED          → "HPA fortsetzen"
 *   SYNTHESIS       → "Profil wird erstellt"
 *   MENTOR_REVIEW   → "Profil wird vervollständigt"
 *   RELEASED        → "Mein Profil ansehen"
 *
 * Wir prüfen den Profil-Status via /profiles/current. Assessment-Status wird
 * über die im sessionStorage gehaltene assessment_id + /assessments/{id}
 * ermittelt (Backend bleibt Source of Truth).
 */

const SS_ASSESSMENT_KEY = "hpa_active_assessment_id";

function loadAssessmentId() {
  try {
    return window.sessionStorage.getItem(SS_ASSESSMENT_KEY);
  } catch (_e) {
    return null;
  }
}

export default function HpaHome() {
  const [state, setState] = useState({
    loading: true,
    homeState: "NOT_STARTED", // NOT_STARTED|ACTIVE|SYNTHESIS|MENTOR_REVIEW|RELEASED
    profileId: null,
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      // 1) Profil zuerst — sagt uns, ob ein synthesiertes Profil existiert
      try {
        const p = await api.get("/hpa/profiles/current").then((r) => r.data);
        if (!alive) return;
        const life = p.lifecycle_state;
        if (life === "RELEASED") {
          setState({ loading: false, homeState: "RELEASED", profileId: p.profile_id });
          return;
        }
        if (life === "MENTOR_REVIEW") {
          setState({ loading: false, homeState: "MENTOR_REVIEW", profileId: p.profile_id });
          return;
        }
        if (life === "SYNTHESIS") {
          setState({ loading: false, homeState: "SYNTHESIS", profileId: p.profile_id });
          return;
        }
      } catch (err) {
        // 404 = kein Profil → weiter zum Assessment-Check
        if (err?.response?.status !== 404) {
          if (!alive) return;
          setState({ loading: false, homeState: "NOT_STARTED", profileId: null });
          return;
        }
      }
      // 2) Assessment-Status via sessionStorage-ID (best effort)
      const aid = loadAssessmentId();
      if (aid) {
        try {
          const a = await api
            .get(`/hpa/assessments/${aid}`)
            .then((r) => r.data);
          if (!alive) return;
          if (a.lifecycle_state && a.lifecycle_state !== "FINALIZED"
              && a.lifecycle_state !== "ARCHIVED") {
            setState({ loading: false, homeState: "ACTIVE", profileId: null });
            return;
          }
        } catch (_e) {
          // 404 → veralteter Marker; entfernen
          try { window.sessionStorage.removeItem(SS_ASSESSMENT_KEY); } catch (_x) { /* noop */ }
        }
      }
      if (alive) setState({ loading: false, homeState: "NOT_STARTED", profileId: null });
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="hpa hpa-canvas" data-testid="hpa-home">
      <SEO
        title="Meine HPA · SAT Human Potential Analysis"
        description="Deine authentifizierte HPA-Journey innerhalb der SAT Akademie."
        path="/hpa"
      />

      <div className="container-sat max-w-4xl pt-28 pb-24 hpa-thread">
        <div className="pl-6 md:pl-10">
          <div className="hpa-overline mb-6" data-testid="hpa-home-overline">
            MY HPA · HUMAN POTENTIAL ANALYSIS
          </div>

          <h1
            className="hpa-headline text-4xl sm:text-5xl lg:text-6xl mb-10"
            data-testid="hpa-home-headline"
          >
            Dein Potenzial ist mehr <br />
            als eine <em>Zahl</em>.
          </h1>

          <p
            className="hpa-lede text-lg md:text-xl max-w-2xl mb-14"
            data-testid="hpa-home-subline"
          >
            Die Human Potential Analysis hilft dir zu erkennen, welche
            Ressourcen in dir angelegt sind, wie sie sich heute zeigen und wo
            möglicherweise mehr in dir steckt, als du aktuell lebst.
          </p>

          <HpaStatusCta state={state.homeState} profileId={state.profileId} loading={state.loading} />
        </div>

        {/* Ruhige Meta-Karte — bewusst KEIN Radar, KEINE bunten Balken (§ 6) */}
        <div className="pl-6 md:pl-10 mt-24 grid md:grid-cols-3 gap-6">
          <MetaCard
            testid="hpa-home-meta-1"
            title="Was in dir angelegt ist"
            body="Welche Ressourcen dir grundsätzlich zur Verfügung stehen."
          />
          <MetaCard
            testid="hpa-home-meta-2"
            title="Wie es sich heute zeigt"
            body="Wieviel davon in deinem aktuellen Leben tatsächlich sichtbar wird."
          />
          <MetaCard
            testid="hpa-home-meta-3"
            title="Wo mehr möglich wäre"
            body="Feine Spannungsfelder zwischen Potenzial und gelebter Realität."
          />
        </div>
      </div>
    </div>
  );
}

function HpaStatusCta({ state, profileId, loading }) {
  if (loading) {
    return (
      <div
        className="text-[color:var(--hpa-text-mute)] text-xs tracking-[0.28em] uppercase"
        data-testid="hpa-home-loading"
      >
        Deine HPA wird geöffnet …
      </div>
    );
  }
  if (state === "RELEASED") {
    return (
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
        <Link
          to={`/hpa/profile/${profileId || "current"}`}
          className="hpa-btn-primary"
          data-testid="hpa-home-cta-view-profile"
        >
          Mein Profil ansehen
        </Link>
        <span
          className="text-xs uppercase tracking-[0.28em] text-[color:var(--hpa-gold-light)]"
        >
          Profil freigegeben
        </span>
      </div>
    );
  }
  if (state === "MENTOR_REVIEW") {
    return (
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
        <Link
          to="/hpa/review-pending"
          className="hpa-btn-primary"
          data-testid="hpa-home-cta-review-pending"
        >
          Status ansehen
        </Link>
        <span
          className="text-xs uppercase tracking-[0.28em] text-[color:var(--hpa-text-soft)]"
        >
          Profil wird vervollständigt
        </span>
      </div>
    );
  }
  if (state === "SYNTHESIS") {
    return (
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
        <Link
          to="/hpa/review-pending"
          className="hpa-btn-primary"
          data-testid="hpa-home-cta-synthesis"
        >
          Profil wird erstellt
        </Link>
      </div>
    );
  }
  if (state === "ACTIVE") {
    return (
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
        <Link
          to="/hpa/assessment"
          className="hpa-btn-primary"
          data-testid="hpa-home-cta-resume"
        >
          HPA fortsetzen
        </Link>
        <span
          className="text-xs uppercase tracking-[0.28em] text-[color:var(--hpa-text-soft)]"
        >
          Deine bisherigen Antworten sind gespeichert
        </span>
      </div>
    );
  }
  // NOT_STARTED
  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
      <Link
        to="/hpa/intro"
        className="hpa-btn-primary"
        data-testid="hpa-home-cta-start"
      >
        Meine HPA starten
      </Link>
      <span
        className="text-xs uppercase tracking-[0.28em] text-[color:var(--hpa-text-mute)]"
        data-testid="hpa-home-hint"
      >
        Preview · nur für berechtigte SAT-Nutzer
      </span>
    </div>
  );
}

function MetaCard({ testid, title, body }) {
  return (
    <div className="hpa-card" data-testid={testid}>
      <div
        className="hpa-overline mb-4"
        style={{ fontSize: "10px", letterSpacing: "0.3em" }}
      >
        Perspektive
      </div>
      <div className="hpa-headline text-xl mb-3" style={{ fontWeight: 400 }}>
        {title}
      </div>
      <p className="hpa-lede text-[15px]" style={{ fontStyle: "normal" }}>
        {body}
      </p>
    </div>
  );
}
